import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { VersionUpdaterService } from '../version-updater.service';

export interface TerminalsDialogData {
  shopId: string;
  shopCode: string;
  shopName: string;
  versionId?: string;
  version?: string; // Add the version string for payload
}

@Component({
  selector: 'app-terminals-by-shop',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule
  ],
  templateUrl: './terminals-by-shop.component.html',
  styleUrl: './terminals-by-shop.component.scss'
})
export class TerminalsByShopComponent implements OnInit {
  terminals: any[] = [];
  isLoading = true;
  isSaving = false; // Add saving state
  error: string = '';

  constructor(
    public dialogRef: MatDialogRef<TerminalsByShopComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TerminalsDialogData,
    private versionUpdaterService: VersionUpdaterService
  ) {}

  ngOnInit(): void {
    this.loadTerminals();
  }

  loadTerminals(): void {
    this.isLoading = true;
    this.error = '';
    
    this.versionUpdaterService.getTerminalsByShop(this.data.shopId, this.data.versionId || '').subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          const assignedTerminals = response.data.assigned_terminals || [];
          const successTerminals = response.data.success_terminal || [];
          
          // Create a Set of all assigned terminal IDs for efficient lookup
          const assignedTerminalIds = new Set<string>();
          assignedTerminals.forEach((assignment: any) => {
            if (assignment.all_assigned_terminals) {
              const terminalIds = assignment.all_assigned_terminals.split(',');
              terminalIds.forEach((id: string) => assignedTerminalIds.add(id.trim()));
            }
          });
          
          // Create a Set of success terminal codes for efficient lookup
          const successTerminalCodes = new Set<string>(successTerminals);
          
          this.terminals = (response.data.terminals || []).map((terminal: any) => ({
            ...terminal,
            selected: assignedTerminalIds.has(terminal.id), // Set selected if terminal ID is in assigned list
            isSuccessTerminal: successTerminalCodes.has(terminal.code) // Check if terminal code is in success_terminal array
          }));
        } else {
          this.error = response.message || 'Failed to load terminals';
        }
      },
      error: (error) => {
        console.error('Error loading terminals:', error);
        this.error = 'Failed to connect to server';
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onSave(): void {
    // Get selected terminal IDs
    const selectedTerminalIds = this.terminals
      .filter(terminal => terminal.selected)
      .map(terminal => terminal.id);

    // Create payload according to the specified format
    const payload = {
      shopTerminalCountForVersion: this.data.version || this.data.versionId || '',
      shopId: this.data.shopId,
      shopCode: this.data.shopCode,
      shopTerminals: selectedTerminalIds // Send terminal IDs directly, not shop IDs
    };

    this.isSaving = true;
    this.error = '';

    this.versionUpdaterService.saveTerminalAssignments(payload).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          // Close dialog with success result
          this.dialogRef.close({ 
            saved: true, 
            terminals: this.terminals.filter(terminal => terminal.selected),
            message: 'Terminal assignments saved successfully'
          });
        } else {
          this.error = response.message || 'Failed to save terminal assignments';
          this.isSaving = false;
        }
      },
      error: (error) => {
        console.error('Error saving terminal assignments:', error);
        this.error = 'Failed to save terminal assignments';
        this.isSaving = false;
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  // Helper method to get selected terminals count
  getSelectedCount(): number {
    return this.terminals.filter(terminal => terminal.selected).length;
  }

  // Helper method to select/deselect all terminals
  toggleSelectAll(): void {
    const allSelected = this.terminals.every(terminal => terminal.selected);
    this.terminals.forEach(terminal => terminal.selected = !allSelected);
  }
}
